import { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import {
  useDocuments,
  useArchiveDocument,
  useUnarchiveDocument,
  useDownloadUploadedDocument,
  useDeleteDocument,
} from '@/hooks/api'
import {
  useAllGeneratedDocuments,
  useDownloadDocument,
  useUpdateDocumentStatus,
  useDeleteGeneratedDocument,
} from '@/hooks/api/useDocumentGeneration'
import { DOCUMENTS_PER_PAGE, GENERATED_DOCS_PER_PAGE } from './DocumentsPage.static'
import type { Document, GeneratedDocumentStatus } from '@/types'

export function useDocumentsPage() {
  const [inputValue, setInputValue] = useState('')
  const [search, setSearch] = useState('')
  const [showArchive, setShowArchive] = useState(false)
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false)
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null)
  const [deleteTargetType, setDeleteTargetType] = useState<'uploaded' | 'generated' | null>(null)
  const [editTargetDoc, setEditTargetDoc] = useState<Document | null>(null)
  const [uploadedPage, setUploadedPage] = useState(1)
  const [generatedPage, setGeneratedPage] = useState(1)
  const { t } = useTranslation('documents')

  const handleSearch = useCallback(() => {
    setSearch(inputValue)
    setUploadedPage(1)
    setGeneratedPage(1)
  }, [inputValue])

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleSearch()
  }

  const uploadedQuery = useDocuments({
    page: uploadedPage,
    limit: DOCUMENTS_PER_PAGE,
    ...(search && { search }),
    ...(showArchive && { isArchived: true }),
  })
  const generatedQuery = useAllGeneratedDocuments({
    page: generatedPage,
    limit: GENERATED_DOCS_PER_PAGE,
    ...(search && { search }),
    ...(showArchive && { isArchived: true }),
  })

  const uploadedDocs = uploadedQuery.data?.documents ?? []
  const uploadedTotal = uploadedQuery.data?.total ?? 0
  const uploadedTotalPages = Math.ceil(uploadedTotal / DOCUMENTS_PER_PAGE)

  const generatedDocs = generatedQuery.data?.documents ?? []
  const generatedTotal = generatedQuery.data?.total ?? 0
  const generatedTotalPages = Math.ceil(generatedTotal / GENERATED_DOCS_PER_PAGE)

  const downloadUploadedMutation = useDownloadUploadedDocument()
  const archiveUploadedMutation = useArchiveDocument()
  const unarchiveUploadedMutation = useUnarchiveDocument()
  const deleteUploadedMutation = useDeleteDocument()
  const downloadGeneratedMutation = useDownloadDocument()
  const archiveGeneratedMutation = useUpdateDocumentStatus()
  const deleteGeneratedMutation = useDeleteGeneratedDocument()

  const handleToggleArchive = () => {
    setShowArchive((prev) => !prev)
    setUploadedPage(1)
    setGeneratedPage(1)
  }

  const handleDownloadUploaded = (id: string) => downloadUploadedMutation.mutate(id)
  const handleArchiveUploaded = (id: string) => archiveUploadedMutation.mutate(id)
  const handleUnarchiveUploaded = (id: string) => unarchiveUploadedMutation.mutate(id)

  const handleDownloadGenerated = (id: string) => downloadGeneratedMutation.mutate(id)
  const handleArchiveGenerated = (id: string) =>
    archiveGeneratedMutation.mutate({
      documentId: id,
      data: { status: 'ARCHIVED' as GeneratedDocumentStatus },
    })
  const handleUnarchiveGenerated = (id: string) =>
    archiveGeneratedMutation.mutate({
      documentId: id,
      data: { status: 'GENERATED' as GeneratedDocumentStatus },
    })

  const openDeleteUploaded = (id: string) => {
    setDeleteTargetId(id)
    setDeleteTargetType('uploaded')
  }
  const openDeleteGenerated = (id: string) => {
    setDeleteTargetId(id)
    setDeleteTargetType('generated')
  }
  const closeDelete = () => {
    setDeleteTargetId(null)
    setDeleteTargetType(null)
  }
  const handleConfirmDelete = () => {
    if (!deleteTargetId || !deleteTargetType) return
    if (deleteTargetType === 'uploaded') {
      deleteUploadedMutation.mutate(deleteTargetId, { onSettled: closeDelete })
    } else {
      deleteGeneratedMutation.mutate(deleteTargetId, { onSettled: closeDelete })
    }
  }

  return {
    t,
    uploadedDocs,
    generatedDocs,
    inputValue,
    search,
    showArchive,
    uploadDialogOpen,
    deleteTargetId,
    isLoadingUploaded: uploadedQuery.isLoading,
    isLoadingGenerated: generatedQuery.isLoading,
    isErrorUploaded: uploadedQuery.isError,
    isErrorGenerated: generatedQuery.isError,
    uploadedPage,
    uploadedTotalPages,
    uploadedTotal,
    generatedPage,
    generatedTotalPages,
    generatedTotal,
    setUploadedPage,
    setGeneratedPage,
    setInputValue,
    handleSearch,
    handleSearchKeyDown,
    setUploadDialogOpen,
    handleToggleArchive,
    handleDownloadUploaded,
    handleArchiveUploaded,
    handleUnarchiveUploaded,
    openDeleteUploaded,
    handleDownloadGenerated,
    handleArchiveGenerated,
    handleUnarchiveGenerated,
    openDeleteGenerated,
    closeDelete,
    handleConfirmDelete,
    editTargetDoc,
    setEditTargetDoc,
  }
}
